import { useEffect, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { emitSessionEndAndSettle } from "../state/sessionEndSignal";
import { isAlwaysReportPath } from "../config/sentryFilters";
import { isTeardownInFlight, markTeardown } from "../utils/teardownState";
import {
  isTransportBurstInFlight,
  markTransportReported,
} from "./transportBurst";
import { markAuthFailureReported } from "./authFailureMarker";
// Issue #672. Distinct from authFailureMarker above, which stamps a Symbol on
// a Response for Sentry de-duplication: a Response-scoped Symbol cannot survive
// the full-document navigation below, and the login page reads its notice from
// sessionStorage.
import {
  markSessionEndReasonIfUnset,
  spendPriorSessionNote,
  SESSION_END_REASON_SESSION_ENDED,
} from "../config/sessionTimeout";
import * as Sentry from "@sentry/react";

const BASE_URL_V1 =
  process.env.REACT_APP_BASE_URL || "https://stg2-api.compasscare.app/api/v1";

const BASE_URL_V2 = BASE_URL_V1.includes("v1")
  ? BASE_URL_V1.replace("v1", "v2")
  : BASE_URL_V1 + "/v2";

const BASE_URL_V3 = BASE_URL_V1.includes("v1")
  ? BASE_URL_V1.replace("v1", "v3")
  : BASE_URL_V1 + "/v3";

// GMA-1216: paths where the web client will honour the backend's silent
// refresh contract (X-Auth-Token header on 2xx, 401 retry-after-refresh).
const isCallApiPath = (formattedPath) => formattedPath.startsWith("call/");

const breadcrumb = (message, data = {}) => {
  try {
    Sentry.addBreadcrumb({
      category: "auth.silent_refresh",
      level: "info",
      message,
      data,
    });
  } catch (_ignored) {
    // Sentry not initialised (tests, local dev) — swallow.
  }
};

const maybePersistRotatedToken = (response) => {
  if (!response?.headers?.get) return;
  const rotated = response.headers.get("X-Auth-Token");
  if (rotated) {
    localStorage.setItem("token", rotated);
    breadcrumb("auth.silent_refresh.succeeded", { source: "x-auth-token" });
  }
};

/**
 * `signingOut` — GMA-1909. A request made while the session is being torn down.
 *
 * Two things must not happen during a sign-out, and both are the DEFAULT
 * behaviour of this function:
 *
 *   1. Persisting a rotated token. v3 `call/*` responses carry `X-Auth-Token`,
 *      which is written straight back to localStorage. A teardown response
 *      arriving after logout() has cleared storage would therefore RESURRECT
 *      authentication - a signed-out tab holding a fresh token.
 *   2. Refreshing on a 401. Refresh mints and stores credentials, which is the
 *      same resurrection by another route, and there is nothing to recover for
 *      a request whose whole purpose is to close something down.
 *
 * The caller must also pass `accessToken`, captured BEFORE storage is cleared:
 * this function normally awaits `getValidAccessToken()`, which reads storage,
 * and that await lands after the clear.
 */
/** Issue #628 — a request path carries client identifiers
 *  (`/clients/27/activities`). Telemetry gets the TEMPLATE, never the id:
 *  numeric segments become `:id` and any query string is dropped, because a
 *  caregiver product must not put a client identifier in a breadcrumb, an
 *  exception, or anything else that leaves the browser. */
export const scrubApiPath = (rawUrl) => {
  const withoutQuery = String(rawUrl).split("?")[0];
  return withoutQuery.replace(/\/\d+(?=\/|$)/g, "/:id");
};

/** Issue #628 — apiRequest returned a non-2xx Response unexamined (a 502 is a
 *  RESOLVED fetch, so it never reached the catch) and its transport catch
 *  threw a generic message with no capture. An API failure was therefore
 *  reported only when a caller forgot to catch it — and #618 proposes adding
 *  the catch that would remove the last such signal.
 *
 *  Capture is deliberately asymmetric:
 *    5xx / transport -> capture. A server error or an unreachable server is
 *                       never a normal flow.
 *    4xx             -> breadcrumb only. #354 removed a guaranteed-401 request
 *                       BECAUSE it polluted Sentry; capturing 4xx here would
 *                       reintroduce exactly that noise by another door. */
/** Issue #628 — the marker that distinguishes the two populations #589 cares
 *  about. Incidental fetch noise from anywhere in the app is still dropped;
 *  a failure apiRequest deliberately reported is kept. Without it the capture
 *  below is made and then discarded by beforeSend on every route except
 *  /register, which is the whole outage class this issue is about. */
export const API_REPORTED_TAG = "api_reported";

const reportApiFailure = ({
  method,
  url,
  status = null,
  error = null,
  // #702 review: a sign-out teardown request is NEVER burst-suppressed.
  // `logout()` awaits emitSessionEndAndSettle (bounded at 1500 ms) and the
  // call page POSTs `call/end` in that gap; a failed `call/end` can leave a
  // call live on the server, which is safety-adjacent telemetry this product
  // must not lose to a noise filter (AuthContext.teardownMark.test.jsx pins
  // it). The same reasoning that exempts /register from the teardown skip.
  signingOut = false,
}) => {
  // Issue #628: an AbortError is a caregiver navigating away, not an outage.
  // beforeSend drops the abort EVENT, but a breadcrumb added here lives on the
  // Sentry scope and is transmitted with the NEXT, unrelated event — so the
  // cancellation was being recorded as a transport failure on someone else's
  // error. The reporter has to skip it, not rely on the event filter.
  if (error && (error.name === "AbortError" || /AbortError/.test(String(error)))) {
    return;
  }
  // Issue #637: the same cancellation, in WebKit. Safari reports a fetch killed
  // by navigation or page teardown as `TypeError: Load failed` — its wording
  // for a real outage too — so the AbortError skip above, which matches
  // Chromium's shape, never fires for it. Measured in
  // COMPASSCARE-FRONTEND-1V: an idle logout redirected to /login and 9 ms
  // later the in-flight poll died, captured at error level and notified
  // members. The message cannot discriminate teardown from outage here, so the
  // discriminator is app state.
  //
  // Unlike the AbortError case this only downgrades the CAPTURE. The
  // breadcrumb still goes on the scope, because an abort is a request that
  // never happened whereas this one really did fail — the record is worth
  // keeping, and a breadcrumb notifies nobody.
  //
  // Two deliberate narrowings, each of which was a way to get this wrong:
  //   - TRANSPORT failures only. A 5xx is a resolved response from a reachable
  //     server, which page teardown cannot manufacture; skipping those too
  //     would hide real server errors during every sign-out.
  //   - NOT on the always-report routes. The always-report policy must win
  //     whenever teardown state is active, whatever set it: /register calls
  //     logout() on a successful registration (RegisterUserPage.jsx; #785
  //     removed the mount-time call) but logout() no longer marks
  //     teardown, so the window there is opened only by an independent source
  //     such as pagehide or a concurrent session-end navigation. Without this
  //     clause such a coincidence would re-hide the exact outage class
  //     #589/#597 were filed for — a real caregiver failure with zero
  //     telemetry.
  const isAlwaysReport = isAlwaysReportPath(
    typeof window !== "undefined" ? window.location.pathname : "",
  );
  // #705 review: `signingOut` exempts from the TEARDOWN skip as well as the
  // burst window. #637 deliberately marks no teardown inside `logout()` so that
  // the session-end `call/end` stays reportable — but the mark has independent
  // sources (`pagehide`, a concurrent session-end navigation), so closing the
  // window mid-sign-out re-hid exactly the request that rationale protects. The
  // safety argument does not depend on WHAT set the state: a failed `call/end`
  // can leave a call live on the server either way.
  const teardownKilled =
    Boolean(error) && isTeardownInFlight() && !isAlwaysReport && !signingOut;
  // Issue #702: the second and later transport failures of one interruption are
  // breadcrumb-only. TRANSPORT failures only — a 5xx keeps reporting, as under
  // #637, because a live server's error is not the blip. The always-report
  // routes win here exactly as they win over teardown: whatever opened the
  // window, a dead /register call must still reach Sentry (#589/#597).
  const burstSuppressed =
    Boolean(error) &&
    !isAlwaysReport &&
    !signingOut &&
    isTransportBurstInFlight();
  const path = scrubApiPath(url);
  try {
    Sentry.addBreadcrumb({
      category: error ? "api.request.transport_failed" : "api.response.non_2xx",
      level: error || status >= 500 ? "error" : "warning",
      message: `${method} ${path}${status === null ? "" : ` -> ${status}`}`,
      data: {
        method,
        path,
        ...(status === null ? {} : { status }),
        // #702: the record of a suppressed failure is kept and labelled, so a
        // burst is legible on whatever event follows it. A breadcrumb notifies
        // nobody, which is the whole point.
        ...(burstSuppressed ? { suppressed: "burst" } : {}),
      },
    });
    if ((error || status >= 500) && !teardownKilled && !burstSuppressed) {
      Sentry.captureException(
        error || new Error(`API ${status} for ${method} ${path}`),
        {
          tags: {
            api_path: path,
            api_method: method,
            [API_REPORTED_TAG]: "true",
          },
          // Issue #702: one stable fingerprint for the whole transport class,
          // so seven callers dying in one connectivity blip share ONE issue
          // instead of opening seven. Scoped to `error` deliberately — a 5xx is
          // a resolved response from a reachable server and its own defect, so
          // it keeps the default (stack) fingerprint and stays separable.
          ...(error && !isAlwaysReport ? { fingerprint: ["api.transport_failed"] } : {}),
        },
      );
      // Only a real capture opens the window. A teardown-killed failure never
      // reaches here, which is what stops a sign-out from silencing the next
      // genuine failure; an always-report route is excluded above for the
      // mirror-image reason.
      if (error && !isAlwaysReport && !signingOut) markTransportReported();
    }
  } catch (_ignored) {
    // Sentry not initialised (tests, local dev) — never let telemetry break a
    // request.
  }
};

/** #635 review — getValidAccessToken() runs BEFORE the transport try, so its
 *  failures (missing/malformed token, refresh failure) were the one apiRequest
 *  class reported nowhere: visible only as an unhandled rejection wherever a
 *  caller forgot to catch, and #618's calendar catch removes exactly that
 *  signal. Same scrubbing and api_reported marker policy as reportApiFailure;
 *  captured, not just breadcrumbed, because a token-acquisition failure on a
 *  request the app chose to make is never a normal flow. */
const reportTokenFailure = ({ method, url, error }) => {
  const path = scrubApiPath(url);
  // The message can embed a URL of its own (e.g. a refresh endpoint) — scrub
  // it with the same template rule rather than trusting its shape.
  const reason = scrubApiPath(error?.message || "unknown");
  try {
    Sentry.addBreadcrumb({
      category: "api.request.token_failed",
      level: "error",
      message: `${method} ${path}`,
      data: { method, path, reason },
    });
    // refreshAccessToken is single-flight, so ONE rejection object reaches
    // every request that awaited it — capturing per caller turns one dead
    // refresh into a burst of identical events (a page load makes dozens of
    // requests). First reporter wins; the breadcrumb above stays per-request
    // because each names the path that was denied.
    // A session END (absent or server-rejected refresh token — marked at the
    // throw site) is the designed sign-out path, not an outage: capturing it
    // would emit an event per idle-expired session, the exact pollution #354
    // removed. The breadcrumb above still records it on whatever comes next.
    if (!error?.isSessionEnd && !error?.sentryTokenFailureReported) {
      if (error) error.sentryTokenFailureReported = true;
      Sentry.captureException(
        new Error(`API token failure for ${method} ${path}`),
        {
          tags: {
            api_path: path,
            api_method: method,
            [API_REPORTED_TAG]: "true",
          },
          extra: { reason },
        },
      );
    }
  } catch (_ignored) {
    // Sentry not initialised (tests, local dev) — never let telemetry break a
    // request.
  }
};

/** The user-facing text for an unreachable server. Exported because it was
 *  written out THREE times -- twice here and once more in
 *  config/capturePanelFailure.js, which deduped centrally-reported transport
 *  failures by comparing against its own copy. Nothing enforced that the copies
 *  matched, so editing the wording here would silently have given every panel a
 *  second event and a misleading `network` breadcrumb (review finding, #666). */
export const API_NETWORK_MESSAGE =
  "Network error: Unable to reach server. Please check your connection.";

/** Callers that classify their own telemetry need to recognise a transport
 *  failure WITHOUT string-matching the message above -- same reason
 *  `isAuthFailure` exists a few lines down, and the same shape. `reportApiFailure`
 *  has already sent this one; the flag is how a caller knows not to send it
 *  again. */
const makeNetworkError = () =>
  Object.assign(new Error(API_NETWORK_MESSAGE), { isNetworkFailure: true });

/** #635 review round 3 — one acquisition path for BOTH request helpers, so a
 *  token failure is reported and marked identically wherever it happens.
 *  apiRequestFormData used to bypass reportTokenFailure entirely, and its
 *  image-upload callers catch-and-log, which left that class with no Sentry
 *  event at all. */
const acquireTokenOrReport = async ({ method, url }) => {
  try {
    return await getValidAccessToken();
  } catch (tokenError) {
    console.error(`[API] Token error for ${method} ${url}:`, tokenError.message);
    reportTokenFailure({ method, url, error: tokenError });
    const authError = new Error(`Authentication error: ${tokenError.message}`);
    // Callers that classify their own telemetry (the calendar) otherwise see a
    // statusless Error and record `network` — contradicting the
    // api.request.token_failed diagnostics emitted just above.
    authError.isAuthFailure = true;
    if (tokenError?.isSessionEnd) authError.isSessionEnd = true;
    throw authError;
  }
};

export const apiRequest = async (
  path,
  {
    method = "GET",
    body = null,
    headers = {},
    version = "v2",
    accessToken = null,
    signingOut = false,
  } = {}
) => {
  const baseUrl =
    version === "v2"
      ? BASE_URL_V2
      : version === "v3"
      ? BASE_URL_V3
      : BASE_URL_V1;

  const formattedPath = path.startsWith("/") ? path.substring(1) : path;
  const url = `${baseUrl}/${formattedPath}`;

  // A caller that captured the credential ahead of time does not go back to
  // storage for it - during a sign-out, storage is already empty.
  let token = accessToken;
  if (token === null) {
    token = await acquireTokenOrReport({ method, url });
  }

  const doFetch = (bearer) =>
    fetch(url, {
      method,
      headers: {
        ...headers,
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearer}`,
      },
      body: body ? JSON.stringify(body) : null,
    });

  try {
    let response = await doFetch(token);
    if (!signingOut && version === "v3" && isCallApiPath(formattedPath)) {
      try {
        maybePersistRotatedToken(response);
      } catch (_persistErr) {
        // Storage writes can throw in Safari private mode. That is not a
        // transport failure and must not open the #702 burst window.
      }
    }

    // GMA-1375: one-shot 401 → refresh → retry for ANY authenticated
    // request. Previously (GMA-1216) this was scoped to v3 + call/*, which
    // left the v1 client-list fetch (clients / caregivers/:id/clients)
    // unable to recover from a token-expiry race — so an expired token at
    // the wrong moment showed an empty client list instead of retrying.
    // A 401 means the request was auth-rejected (never ran server-side),
    // so re-sending once after a refresh is safe for any method/version.
    // Stays one-shot (no refresh loop). Rotated-token persistence remains
    // scoped to v3 call/* — only those responses carry X-Auth-Token.
    if (response.status === 401 && !signingOut) {
      breadcrumb("auth.silent_refresh.web_retry", { path: scrubApiPath(formattedPath), version });
      // Issue #628: the catch is scoped to refreshAccessToken() ALONE. It
      // used to wrap the retry as well, so a retry dying at the transport was
      // caught as a refresh failure — breadcrumbed under auth, `response` left
      // as the original 401, and the outage then reported as a warning-level
      // 401. Wrong severity, wrong subsystem, real failure uncaptured. A
      // rejected retry now reaches the outer transport catch, where it belongs.
      let refreshed = null;
      let refreshOk = false;
      try {
        refreshed = await refreshAccessToken();
        refreshOk = true;
      } catch (refreshError) {
        breadcrumb("auth.silent_refresh.failed", {
          path: scrubApiPath(formattedPath),
          reason: refreshError?.message || "unknown",
        });
        // #635 review: this catch falls back to the original 401, which is
        // breadcrumb-only (4xx) — so a refresh dying here would otherwise be
        // the one token-failure class with no Sentry event. Same deduplicating
        // reporter as the proactive path; the single-flight rejection object
        // keeps a concurrent burst to one event.
        reportTokenFailure({ method, url, error: refreshError });
        // Issue #673: the fall-back below returns this 401 rather than
        // throwing (option 1 in #673 would change control flow for every
        // caller, session-end redirects among them). parseJsonOrThrow then
        // builds a bare status-401 error, so a caller classifying its own
        // telemetry reported what was just reported here a second time.
        // Stamp the response we fall back to; responseUtils forwards the
        // stamp onto the error. Only marks THIS 401 — a 401 the server
        // issues to a freshly refreshed token (refreshOk) is a real
        // authorization answer and stays unmarked.
        markAuthFailureReported(response);
      }
      if (refreshOk) {
        response = await doFetch(refreshed);
        if (version === "v3" && isCallApiPath(formattedPath)) {
          try {
            maybePersistRotatedToken(response);
          } catch (_persistErr) {
            // Same as the first persist: a storage throw is not transport.
          }
        }
      }
    }

    // Issue #628: report the FINAL response, after the 401 refresh/retry
    // above — reporting mid-flow would breadcrumb every successful refresh.
    if (!response.ok)
      reportApiFailure({ method, url, status: response.status, signingOut });
    return response;
  } catch (networkError) {
    console.error(`[API] Network error for ${method} ${url}:`, networkError.message);
    reportApiFailure({ method, url, error: networkError, signingOut });
    throw makeNetworkError();
  }
};

export const apiRequestFormData = async (
  path,
  { method = "POST", body = null, headers = {}, version = "v2" } = {}
) => {
  const baseUrl =
    version === "v2"
      ? BASE_URL_V2
      : version === "v3"
      ? BASE_URL_V3
      : BASE_URL_V1;

  const formattedPath = path.startsWith("/") ? path.substring(1) : path;
  const url = `${baseUrl}/${formattedPath}`;
  const token = await acquireTokenOrReport({ method, url });

  // GMA-1375 (Copilot review): authenticated FormData uploads must also
  // recover from a token-expiry race, so apply the same one-shot
  // 401 → refresh → retry as apiRequest. Don't set Content-Type — the
  // browser sets it with the multipart boundary.
  const doFetch = (bearer) =>
    fetch(url, {
      method,
      headers: {
        ...headers,
        Authorization: `Bearer ${bearer}`,
      },
      body: body,
    });

  // Issue #628: mirrors apiRequest exactly. Adding only the non-2xx report
  // here left BOTH transport paths uncovered — an initial rejection never
  // reached that line, and a retry rejection was swallowed by the refresh
  // catch, leaving the original 401 to be breadcrumbed in its place. An upload
  // against an unreachable server reported nothing, or reported the wrong
  // thing. The catch is scoped to refreshAccessToken() alone for the same
  // reason it is over there.
  try {
    let response = await doFetch(token);
    if (response.status === 401) {
      breadcrumb("auth.silent_refresh.web_retry", {
        path: scrubApiPath(formattedPath),
        version,
        kind: "formdata",
      });
      let refreshed = null;
      let refreshOk = false;
      try {
        refreshed = await refreshAccessToken();
        refreshOk = true;
      } catch (refreshError) {
        breadcrumb("auth.silent_refresh.failed", {
          path: scrubApiPath(formattedPath),
          reason: refreshError?.message || "unknown",
        });
        // #635 review: this catch falls back to the original 401, which is
        // breadcrumb-only (4xx) — so a refresh dying here would otherwise be
        // the one token-failure class with no Sentry event. Same deduplicating
        // reporter as the proactive path; the single-flight rejection object
        // keeps a concurrent burst to one event.
        reportTokenFailure({ method, url, error: refreshError });
        // Issue #673: mirrors apiRequest — see the comment there.
        markAuthFailureReported(response);
      }
      if (refreshOk) {
        response = await doFetch(refreshed);
      }
    }
    // A non-2xx Response is a RESOLVED fetch, so it never enters the catch.
    if (!response.ok) reportApiFailure({ method, url, status: response.status });
    return response;
  } catch (networkError) {
    console.error(
      `[API] Network error for ${method} ${url}:`,
      networkError.message,
    );
    reportApiFailure({ method, url, error: networkError });
    throw makeNetworkError();
  }
};

// GMA-1375 (issue #348): single-flight the refresh. Now that the 401 retry
// covers every authenticated request, an expired token on page load 401s many
// concurrent requests at once. Each used to fire its own refresh; because the
// refresh endpoint ROTATES the refresh token, the second concurrent refresh
// sent an already-invalidated token -> failure -> localStorage.clear() +
// redirect to /login (a spurious force-logout). Sharing one in-flight refresh
// promise makes concurrent 401s await a single rotation.
let refreshInFlight = null;
const refreshAccessToken = () => {
  if (refreshInFlight) {return refreshInFlight;}
  refreshInFlight = doRefreshAccessToken().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

const doRefreshAccessToken = async () => {
  const REFRESH_URL = `${BASE_URL_V2}/users/refresh-token`;

  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) {
    const sessionEndError = new Error("No refresh token available");
    // Session-shaped, not an outage: reportTokenFailure skips capturing these
    // (#354 — an event per idle-expired session is pollution, not signal).
    sessionEndError.isSessionEnd = true;
    throw sessionEndError;
  }

  const email = localStorage.getItem("user_email");
  const expiredToken = localStorage.getItem("token");

  const response = await fetch(REFRESH_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(expiredToken ? { Authorization: `Bearer ${expiredToken}` } : {}),
    },
    body: JSON.stringify({
      refresh_token: refreshToken,
      ...(email ? { email } : {}),
      platform: "web",
    }),
  });

  const data = await response.json();
  if (!data.success) {
    console.error("[AUTH] Refresh token failed — clearing session");
    // Issue #672 — FIRST, before the teardown and before localStorage.clear().
    // The caregiver is about to be dropped on /login with no idea why: this is
    // the one line that lets that page explain itself. It goes in
    // sessionStorage, which `localStorage.clear()` below does not touch, and
    // is written before the await so a surface that hangs its teardown cannot
    // lose the explanation.
    //
    // IfUnset, never a plain mark: this races the idle path. The countdown can
    // fire and write "idle" while a refresh is already in flight, and that
    // refresh resolving `success: false` a moment later must not relabel a
    // thirty-minute absence as a generic session end (PR #698 review).
    markSessionEndReasonIfUnset(SESSION_END_REASON_SESSION_ENDED);
    // This is a session END like the idle and cross-tab paths, and it runs
    // the same teardown protocol BEFORE clearing or navigating (round 22):
    // the standalone /call route arms a beforeunload guard while a call is
    // live, so redirecting without tearing down first raises a prompt whose
    // Cancel strands a signed-out tab on /call with client data visible. A
    // same-tab localStorage.clear() fires no storage event, so the
    // cross-tab handler cannot cover this path. Bounded inside
    // emitSessionEndAndSettle, so a hung surface cannot hold this open. The
    // teardown's own call/end request passes signingOut, which skips this
    // 401-refresh path — no recursion.
    await emitSessionEndAndSettle();
    localStorage.clear();
    // REPLACE, never assign href. /call is outside RequireLogin, so a
    // pushed entry is reachable by Back once the token is gone (round 21).
    //
    // Issue #637 — third of the three session-end navigations, and it kills
    // in-flight polls exactly as the other two do. Marked after the teardown
    // above settles, so a failed `call/end` is still captured.
    markTeardown();
    window.location.replace("/login");
    const sessionEndError = new Error("Refresh token failed");
    // Only an auth-shaped rejection is a session END. A refresh service
    // answering 5xx with success:false is an OUTAGE — it must stay
    // reportable, since this raw fetch never passes through reportApiFailure.
    // (The teardown above still runs for both: on a 5xx the session is gone
    // either way, and a signed-out redirect is the safe failure mode.)
    sessionEndError.isSessionEnd = response.status < 500;
    throw sessionEndError;
  }

  localStorage.setItem("token", data.access_token);
  localStorage.setItem("refresh_token", data.refresh_token || refreshToken);

  return data.access_token;
};

// Issue #354: an authenticated useFetch mount request must not fire for an
// UNAUTHENTICATED visitor. #340 stopped the resulting 401 "Invalid token" from
// becoming an *unhandled* rejection (mount-effect .catch) and gated the
// ClientContext caregivers fetch on currentUser — but a stray/foreign token
// left in localStorage (e.g. a commandcenter demo visitor, or any future caller
// that forgets to gate) still fires the request, 401s, and pollutes Sentry
// (COMPASSCARE-FRONTEND-1K) with what is normal auth flow, not an error.
//
// A real logged-in web session always has BOTH a `token` and a `refresh_token`
// (LoginPage sets both; the refresh flow keeps both; force-logout clears both).
// A visitor with no token, or only a stray `token` that has no refresh_token and
// therefore cannot be refreshed, is effectively unauthenticated. Gating the
// fire-and-forget mount fetch on a real session means no pointless 401 is
// generated for those visitors — while a genuinely authenticated user still
// fires every request, so a real mid-session invalid/expired-token 401 still
// surfaces (error state + the refresh -> force-logout flow).
const hasAuthenticatedSession = () => {
  try {
    return Boolean(
      localStorage.getItem("token") && localStorage.getItem("refresh_token"),
    );
  } catch (_ignored) {
    // localStorage unavailable (private mode / SSR) — treat as no session.
    return false;
  }
};

// #701 / #699 — single-flight the missing-token sign-out. Parallel callers on
// /call (heartbeat + presence + call/end probes) would otherwise each emit
// teardown and race location.replace.
let missingTokenSignOutInFlight = null;

/**
 * Issues #701 / #699 — access token already gone on a surface outside
 * RequireLogin (standalone `/call`). Same ordering as refresh-failure
 * force-logout: mark IfUnset, settle teardown, clear, markTeardown, replace.
 * Gated by spendPriorSessionNote so a never-authenticated visitor does
 * not get a false "session ended" notice or a redirect loop.
 */
const endSessionForMissingAccessToken = () => {
  if (missingTokenSignOutInFlight) return missingTokenSignOutInFlight;
  missingTokenSignOutInFlight = (async () => {
    // Reason may already be "idle"; IfUnset preserves it. The gate already
    // marked once — this is a safety net if a peer path raced the write.
    markSessionEndReasonIfUnset(SESSION_END_REASON_SESSION_ENDED);
    await emitSessionEndAndSettle();
    try {
      localStorage.clear();
    } catch (_ignored) {
      // storage may throw in private mode — navigation still required
    }
    markTeardown();
    window.location.replace("/login");
  })().finally(() => {
    missingTokenSignOutInFlight = null;
  });
  return missingTokenSignOutInFlight;
};

const getValidAccessToken = async () => {
  const token = localStorage.getItem("token");
  if (!token) {
    // Prior session → full protocol (covers unguarded /call). No prior session
    // → throw only; signed-out visitors must not be redirected with a notice.
    // Spend the note (not IfUnset's return): idle may already own the reason.
    // Spend the note (not IfUnset's return): idle may already own the reason.
    // Followers that lose the spend still join if sign-out is already in
    // flight — otherwise they look like ordinary token failures and burst
    // reportTokenFailure while teardown is pending.
    const endingSession =
      spendPriorSessionNote() || Boolean(missingTokenSignOutInFlight);
    if (endingSession) {
      markSessionEndReasonIfUnset(SESSION_END_REASON_SESSION_ENDED);
      await endSessionForMissingAccessToken();
    }
    const missing = new Error("No access token available");
    // Only the designed sign-out is quiet for Sentry (#354). A stray caller
    // with no prior session still reports via reportTokenFailure.
    if (endingSession) missing.isSessionEnd = true;
    throw missing;
  }

  const decoded = jwtDecode(token);
  const now = Math.floor(Date.now() / 1000);

  if (decoded.exp < now + 30) {
    return await refreshAccessToken();
  }

  return token;
};

const useRequestState = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Issue #623: `isStale` lets a caller disown a response that a newer request
  // (or an unmount) has superseded. It gates only the STATE WRITES — the
  // promise still resolves/rejects exactly as before, so awaiting callers and
  // the fire-and-forget .catch() chains are unaffected.
  const run = async (asyncFn, isStale = () => false) => {
    setLoading(true);
    try {
      return await asyncFn();
    } catch (err) {
      if (!isStale()) setError(err);
      throw err;
    } finally {
      if (!isStale()) setLoading(false);
    }
  };
  // Issue #623: a skip branch starts no request, so nothing of its own ever
  // settles the spinner. Gating the stale request's finally is correct — a
  // superseded request must not clear a NEWER request's loading — but it
  // leaves `loading` true forever when the superseding effect run takes a skip
  // branch. Skip paths therefore settle explicitly.
  const settle = () => {
    setLoading(false);
    setError(null);
  };
  return { run, loading, error, settle };
};

// Issue #353: emitted when a fire-and-forget refetch() rejection is absorbed
// after already being tracked in the hook's `error` state. Post-deploy
// verification: `error.type:Error message:"Invalid token"` stays empty while
// `category:auth.token.invalid_handled` records every handled occurrence.
const breadcrumbHandledRefetchRejection = (path, err) => {
  try {
    // #675: never put err.message on the scope. parseJsonOrThrow builds that
    // from the response body (alert / PHI-adjacent prose). Status + error class
    // are ours; the body is not.
    const status = typeof err?.status === "number" ? err.status : null;
    Sentry.addBreadcrumb({
      category: "auth.token.invalid_handled",
      level: "warning",
      message: "useFetch refetch rejection handled (fire-and-forget caller)",
      data: {
        path,
        status: status === null ? "unknown" : status,
        errorName: err?.name || "Error",
      },
    });
  } catch (_ignored) {
    // Sentry not initialised (tests, local dev) — swallow.
  }
};

export const useFetch = (path, options = {}, deps = []) => {
  const [data, setData] = useState([]);
  // Issue #623: monotonic request generation. Every effect run and every
  // refetch claims one; cleanup bumps it. A closure whose generation is no
  // longer current owns a superseded response and must not write state.
  const generationRef = useRef(0);
  const { run, loading, error, settle } = useRequestState();

  const fetchData = (isStale = () => false) =>
    run(async () => {
      if (!path) {
        if (!isStale()) setData([]);
        return [];
      }
      const response = await apiRequest(path, options);
      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        // #675: do not log parseError.message — V8 can echo response text.
        console.error(
          `[API] Failed to parse JSON response for ${path}: status ${response.status}`,
        );
        throw Object.assign(
          new Error(`Server returned invalid response (status ${response.status})`),
          { status: response.status },
        );
      }
      if (!response.ok) {
        const errorMsg = result?.message || result?.error || `Request failed with status ${response.status}`;
        // #675: log status only — Sentry's console breadcrumb integration would
        // otherwise put response-body prose on the scope for the next capture.
        console.error(
          `[API] Request failed for ${path}: status ${response.status}`,
        );
        throw Object.assign(new Error(errorMsg), { status: response.status });
      }
      // Issue #623: the ordering gate. A response belonging to a superseded
      // request must never be written — switching client A -> B re-fires this
      // effect, and A resolving after B would otherwise render A's data under
      // B's profile.
      // Falls through with no value, exactly as before this change: the
      // callback's fulfillment value is part of refetch()'s contract for
      // awaiting callers, and the guard must not alter it.
      if (isStale()) return;
      setData(result);
    }, isStale);

  useEffect(() => {
    // Issue #623: the generation is claimed BEFORE the early returns, and every
    // path returns the invalidator. Claiming it after them left the skip paths
    // registering no cleanup at all, so a refetch() started from a skipped
    // state — the post-login case this hook is designed for — was never
    // disowned when the hook later transitioned to another skipped state or
    // unmounted, and its response could still write.
    const myGeneration = ++generationRef.current;
    const isStale = () => generationRef.current !== myGeneration;
    const invalidate = () => {
      generationRef.current += 1;
    };

    if (!path) {
      setData([]);
      settle();
      return invalidate;
    }

    // Issue #354: skip the authed mount fetch entirely for an unauthenticated
    // visitor. Without a real session the request is a guaranteed 401 "Invalid
    // token" — expected auth flow, not an error — so firing it only pollutes
    // Sentry. An explicit refetch() (post-login) is unaffected: by then the
    // session exists. A logged-in user always has a session, so their requests
    // (and any genuine mid-session 401) still fire and surface.
    if (!hasAuthenticatedSession()) {
      setData([]);
      settle();
      return invalidate;
    }

    // Issue #623: `let isCancelled = false` stood here, was tested on the very
    // next line (so the test could never fail) and set true in a cleanup
    // nothing ever read again — a guard that guarded nothing.

    // Issue #339: fetchData() (via useRequestState.run) deliberately
    // re-throws after setting `error` state so awaiting callers can react —
    // but this mount-effect call is fire-and-forget. Without a .catch()
    // here, any failed request (401 with no session, network error) became
    // a genuine unhandled promise rejection on top of the already-captured
    // `error` state. The rejection is already tracked in state; swallow it
    // here so it doesn't ALSO escape as runtime noise.
    fetchData(isStale).catch(() => {});
    return invalidate;
  }, deps);

  // Issue #353: `refetch` is invoked fire-and-forget across the app
  // (ClientContext.registerClient/deleteClient/deleteCaregiver, the
  // assignment/add-caregiver pages, and CallV3Context's onCallEnd callback).
  // fetchData (via useRequestState.run) deliberately re-throws after setting
  // `error` state so awaiting callers can react — but a bare refetch() left
  // that rejection with no handler, escaping as an uncaught
  // `Error: Invalid token ... at async run` (#339 guarded only the mount
  // effect's leg). Attach a background handler that marks the rejection as
  // handled + breadcrumbs it, and return the ORIGINAL promise so
  // `await refetch()` still rejects for callers that legitimately await.
  const refetch = () => {
    // Issue #623: a refetch is a new request and supersedes anything in
    // flight, including a previous refetch.
    const myGeneration = ++generationRef.current;
    const request = fetchData(() => generationRef.current !== myGeneration);
    request.catch((err) => {
      breadcrumbHandledRefetchRejection(path, err);
    });
    return request;
  };

  return { data, loading, error, refetch };
};

export const useMutate = () => {
  const { run, loading, error } = useRequestState();

  const mutate = (path, options) =>
    run(async () => {
      const response = await apiRequest(path, options);
      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        // #675: do not log parseError.message — V8 can echo response text.
        console.error(
          `[API] Failed to parse JSON response for ${path}: status ${response.status}`,
        );
        throw Object.assign(
          new Error(`Server returned invalid response (status ${response.status})`),
          { status: response.status },
        );
      }
      if (!response.ok) {
        const errorMsg = result?.message || result?.error || `Request failed with status ${response.status}`;
        // #675: status only — see useFetch sister log above.
        console.error(
          `[API] Mutation failed for ${path}: status ${response.status}`,
        );
        throw Object.assign(new Error(errorMsg), { status: response.status });
      }
      return result;
    });
  return { mutate, loading, error };
};
